import LocalizedStrings from 'localized-strings';

import { formatAmount } from 'common/formatAmount';

import { RowKey, SectionKey } from './types';

const locales = new LocalizedStrings({
  en: {
    illimited: 'Unlimited',
    isComing: 'Coming soon',
    new: 'New',
    optionnal: 'Optional',
    perCard: ' / card',
    perMonth: ' / month',
    perPerson: 'Limited to 1 card / person',
    prioritySupport: 'Priority',
    rows: {
      [RowKey.PhysicalCards]: {
        subtitle:
          'Pay anywhere in the world and configure your cards based on your needs.',
        title: 'Physical cards',
      },
      [RowKey.VirtualCards]: {
        subtitle:
          'Generate virtual cards, permanent or temporary, to secure your online purchases.',
        title: 'Virtual cards',
      },
      [RowKey.Transfers]: {
        subtitle:
          'Instantly receive the transfers in your account. € 0.40 excl. tax per additional transaction.',
        title: 'SEPA transfers and direct debits',
      },
      [RowKey.MaximumPaymentMonth]: {
        subtitle:
          'Maximum amount authorized per card, within the limit of € 10,000 over 7 days in Shine Basic or € 15,000 in Shine Plus, Pro and Business.',
        title: 'Payment limit per card over 30 days',
      },
      [RowKey.MaximumWithdrawalMonth]: {
        subtitle: 'Maximum amount authorized per card, over 30 days.',
        title: 'Withdrawal limit over 30 days',
      },
      [RowKey.WithdrawalMonth]: {
        subtitle: `Withdraw your cash free of charge from any ATM in the euro area. Then an additional ${formatAmount(
          {
            forcePlusSign: false,
            value: 1,
            withNoMinorUnitDigits: true,
          },
        )} excl. tax per withdrawal.`,
        title: 'Withdrawals',
      },
      [RowKey.BulkTransfers]: {
        subtitle: 'Make up to 100 transfers at once.',
        title: 'Bulk transfers',
      },
      [RowKey.SwiftTransfer]: {
        subtitle:
          'Send and receive the transfers outside the euro area (SWIFT).',
        title: 'International SWIFT transfers',
      },
      [RowKey.CheckDepositMonth]: {
        subtitle: `Cash your checks in a few clicks. Additional collection at ${formatAmount(
          {
            forcePlusSign: false,
            value: 2,
            withNoMinorUnitDigits: true,
          },
        )} / check excl. tax.`,
        title: 'Check deposit',
      },
      [RowKey.CashDepositMonth]: {
        subtitle:
          'Instantly receive the cash in your account, up to €9,500/month. 4% charges applied beyond the indicated amounts.',
        title: 'Cash deposit',
      },
      [RowKey.AccessManagement]: {
        subtitle:
          'Make team expenditures easier thanks to dedicated access for your employees.',
        title: 'Additional access',
      },
      [RowKey.CardsManagement]: {
        subtitle:
          'Give autonomy to your team: Validate the card requests in one click and simplify your company expenses.',
        title: 'Card application management',
      },
      [RowKey.Settings]: {
        subtitle:
          'Maintain control of the expenses of your employees, adjust the limits at all times.',
        title: 'Adjustable parameters',
      },
      [RowKey.SingleUsageCard]: {
        subtitle: 'Award temporary virtual cards for one-off expenses.',
        title: 'Single-use cards',
      },
      [RowKey.AccountantAccess]: {
        subtitle:
          'Invite your accountant to directly access transactions, receipts and exports.',
        title: 'Dedicated accounting access',
      },
      [RowKey.Receipts]: {
        subtitle: 'Easily add your receipts directly to your transactions.',
        title: 'Addition of receipts in 1 click',
      },
      [RowKey.AccountantExports]: {
        subtitle:
          'Easily generate a complete export of your transactions and receipts. All of your history is preserved.',
        title: 'Unlimited accounting exports',
      },
      [RowKey.AccountantSoftwareIntegration]: {
        subtitle:
          'Automate your accounting by connecting your Shine account to your accounting software.',
        title: 'Accounting software integrations',
      },
      [RowKey.BillingTool]: {
        subtitle:
          'Create and send professional estimates and invoices in a few clicks.',
        title: 'Complete invoicing tool',
      },
      [RowKey.AutoReminder]: {
        subtitle:
          'Work with peace of mind and let Shine send reminders to your customers in case of late payment.',
        title: 'Automatic reminders',
      },
      [RowKey.InstantPayment]: {
        subtitle:
          'Get paid faster by asking your client to pay you online by card.',
        title: 'Instant payment per card of your invoices',
      },
      [RowKey.PaymentTerminal]: {
        subtitle:
          'Choose the terminal most adapted to your needs, at a special rate.',
        title: 'Several optional TPE offers',
      },
      [RowKey.OnlineSupport]: {
        subtitle:
          'Our France-based team will respond to all your questions 7 days a week.',
        title: 'Online support',
      },
      [RowKey.PhoneSupport]: {
        subtitle: 'Have a Shine advisor call you back if necessary. ',
        title: 'Telephone support',
      },
      [RowKey.LegalAssistance]: {
        subtitle:
          'Ask all your legal questions to lawyers, available to you by phone 6 days a week.',
        title: 'Legal assistance',
      },
      [RowKey.ComputerInsurance]: {
        subtitle:
          'Get up to €3,000 reimbursed if your business laptop breaks or is stolen, up to once a year.',
        title: 'Laptop insurance',
      },
      [RowKey.RecoveryAssistance]: {
        subtitle:
          'Get answers about your unpaid invoices, with lawyers, available 6 days a week by phone.',
        title: 'Debt collection assistance',
      },
      [RowKey.AccidentInsurance]: {
        subtitle: `Receive a compensation of ${formatAmount({
          forcePlusSign: false,
          value: 100,
          withNoMinorUnitDigits: true,
        })} per day of hospitalization in the event of an accident.`,
        title: 'Accident cover',
      },
      [RowKey.MobileInsurance]: {
        subtitle: `Get reimbursements of up to ${formatAmount({
          forcePlusSign: false,
          value: 150,
          withNoMinorUnitDigits: true,
        })} if your screen breaks, with a limit of one repair per year.`,
        title: 'Phone protection',
      },
      [RowKey.WarrantyExtension]: {
        subtitle:
          'Double the manufacturer’s warranty period for your devices purchased with a Shine card.',
        title: 'Extension of the warranty',
      },
      [RowKey.ContractLibrary]: {
        subtitle:
          'Access templates of legal contracts and financial documents to start and develop your company.',
        title: 'Access to a library of contracts',
      },
      [RowKey.PartnerNetwork]: {
        subtitle: 'Find an accountant specializing in your domain.',
        title: 'Network of partner accountants',
      },
      [RowKey.ToolBox]: {
        subtitle:
          'Access exclusive partner offers: insurance, accounting software, investments...',
        title: 'Partners',
      },
    },
    sections: {
      [SectionKey.BankAndPayment]: 'Bank and payments',
      [SectionKey.SpendManagement]: 'Expense management',
      [SectionKey.SimplifiedComptability]: 'Simplified accounting',
      [SectionKey.Billing]: 'Invoicing',
      [SectionKey.SupportAndInsurance]: 'Assistance and insurance',
    },
    standardSupport: 'Standard',
    virtualCardsIncluded: 'virtual cards included',
  },
  fr: {
    illimited: 'Illimitées',
    isComing: 'À venir',
    new: 'Nouveau',
    optionnal: 'En option',
    perCard: ' / carte',
    perMonth: ' / mois',
    perPerson: 'Limité à 1 carte / personne',
    prioritySupport: 'Prioritaire',
    rows: {
      [RowKey.PhysicalCards]: {
        subtitle:
          'Payez partout dans le monde et paramétrez vos cartes en fonction de vos besoins.',
        title: 'Cartes physiques',
      },
      [RowKey.VirtualCards]: {
        subtitle:
          'Générez des cartes virtuelles, permanentes ou éphémères, pour sécuriser vos achats en ligne.',
        title: 'Cartes virtuelles',
      },
      [RowKey.Transfers]: {
        subtitle:
          'Recevez instantanément les virements sur votre compte. 0,40 € HT par mouvement supplémentaire.',
        title: 'Virements et prélèvements SEPA',
      },
      [RowKey.MaximumPaymentMonth]: {
        subtitle:
          'Montant maximum autorisé par carte, dans la limite de 10 000 € sur 7 jours en Shine Basic ou 15 000 € en Shine Plus, Pro et Business.',
        title: 'Plafond de paiement par carte sur 30 jours',
      },
      [RowKey.MaximumWithdrawalMonth]: {
        subtitle: 'Montant maximum autorisé par carte, sur 30 jours glissants.',
        title: 'Plafond de retrait sur 30 jours',
      },
      [RowKey.WithdrawalMonth]: {
        subtitle: `Retirez vos espèces gratuitement dans tous les distributeurs en zone euro. Puis ${formatAmount(
          {
            forcePlusSign: false,
            value: 1,
            withNoMinorUnitDigits: true,
          },
        )} HT par retrait supplémentaire.`,
        title: 'Retraits',
      },
      [RowKey.BulkTransfers]: {
        subtitle: 'Réalisez jusqu’à 100 virements en une seule fois.',
        title: 'Virements groupés',
      },
      [RowKey.SwiftTransfer]: {
        subtitle:
          'Envoyez et recevez des virements en dehors de la zone euro (SWIFT).',
        title: 'Virements internationaux SWIFT',
      },
      [RowKey.CheckDepositMonth]: {
        subtitle: `Encaissez vos chèques en quelques clics. Encaissement additionnel à ${formatAmount(
          {
            forcePlusSign: false,
            value: 2,
            withNoMinorUnitDigits: true,
          },
        )} HT / chèque.`,
        title: 'Dépôt de chèques',
      },
      [RowKey.CashDepositMonth]: {
        subtitle:
          'Recevez vos espèces instantanément sur votre compte, jusqu’à 9 500 € / mois. 4 % de frais appliqués au delà des montants indiqués',
        title: 'Dépôt d’espèces',
      },
      [RowKey.AccessManagement]: {
        subtitle:
          'Facilitez vos dépenses en équipe grâce à des accès dédiés pour vos employé·es.',
        title: 'Accès supplémentaires',
      },
      [RowKey.CardsManagement]: {
        subtitle:
          'Donnez de l’autonomie à votre équipe : validez les demandes de cartes en un clic et simplifiez vos dépenses d’entreprise.',
        title: 'Gestion des demandes de cartes',
      },
      [RowKey.Settings]: {
        subtitle:
          'Gardez la main sur les dépenses de vos employé·es, ajustez les plafonds à tout moment.',
        title: 'Paramètres ajustables',
      },
      [RowKey.SingleUsageCard]: {
        subtitle:
          'Accordez des cartes virtuelles éphémères pour des dépenses  ponctuelles.',
        title: 'Cartes à usage unique',
      },
      [RowKey.ComputerInsurance]: {
        subtitle:
          'Obtenez jusqu’à 3000 € remboursés si votre ordinateur professionnel casse ou s’il est volé, dans la limite d’une fois par an.',
        title: 'Assurance casse / vol ordinateur',
      },
      [RowKey.AccountantAccess]: {
        subtitle:
          'Invitez votre expert-comptable à accéder directement aux transactions, justificatifs et exports.',
        title: 'Accès comptable dédié',
      },
      [RowKey.Receipts]: {
        subtitle:
          'Ajoutez facilement vos reçus directement à vos transactions.',
        title: 'Ajout des reçus en 1 clic',
      },
      [RowKey.AccountantExports]: {
        subtitle:
          'Générez facilement un export complet de vos transactions et justificatifs. Tout votre historique est conservé.',
        title: 'Exports comptable illimités',
      },
      [RowKey.AccountantSoftwareIntegration]: {
        subtitle:
          'Automatisez votre comptabilité en connectant votre compte Shine à votre logiciel comptable.',
        title: 'Intégrations logiciels comptables',
      },
      [RowKey.BillingTool]: {
        subtitle:
          'Créez et envoyez devis et factures professionnelles en quelques clics.',
        title: 'Outil de facturation complet',
      },
      [RowKey.AutoReminder]: {
        subtitle:
          'Travaillez l’esprit libre et laissez Shine relancer vos client·es en cas de retard de paiement.',
        title: 'Relances automatiques',
      },
      [RowKey.InstantPayment]: {
        subtitle:
          'Soyez payé·e plus rapidement en proposant à vos client·es de vous payer en ligne par carte.',
        title: 'Paiement instantané par carte de vos factures',
      },
      [RowKey.PaymentTerminal]: {
        subtitle:
          'Choisissez le terminal le plus adapté à vos besoins, à tarif préférentiel.',
        title: 'Plusieurs offres de TPE en option',
      },
      [RowKey.OnlineSupport]: {
        subtitle:
          'Notre équipe basée en France répond à toutes vos questions 7j/7.',
        title: 'Support en ligne',
      },
      [RowKey.PhoneSupport]: {
        subtitle:
          'Faites-vous rappeler par un·e conseiller·e Shine en cas de besoin.',
        title: 'Support téléphonique',
      },
      [RowKey.LegalAssistance]: {
        subtitle:
          'Posez toutes vos questions légales à des juristes, disponibles pour vous 6 j/7 par téléphone.',
        title: 'Assistance juridique',
      },
      [RowKey.RecoveryAssistance]: {
        subtitle:
          'Obtenez des réponses concernant vos factures impayées, avec des juristes, disponibles 6j/7 par téléphone',
        title: 'Assistance recouvrement de créance',
      },
      [RowKey.AccidentInsurance]: {
        subtitle: `Bénéficiez d’une indemnisation de ${formatAmount({
          forcePlusSign: false,
          value: 100,
          withNoMinorUnitDigits: true,
        })} par jour d'hospitalisation en cas d’accident.`,
        title: 'Garantie accident',
      },
      [RowKey.MobileInsurance]: {
        subtitle: `Obtenez jusqu’à ${formatAmount({
          forcePlusSign: false,
          value: 150,
          withNoMinorUnitDigits: true,
        })} remboursés si votre écran casse, dans la limite d'une réparation par an.`,
        title: 'Protection téléphone',
      },
      [RowKey.WarrantyExtension]: {
        subtitle:
          'Doublez la durée de garantie constructeur de vos appareils achetés avec une carte Shine.',
        title: 'Extension de garantie',
      },
      [RowKey.ContractLibrary]: {
        subtitle:
          'Accédez à des modèles de contrats juridiques et documents financiers pour créer et développer votre entreprise.',
        title: 'Accès à une bibliothèque de contrats',
      },
      [RowKey.PartnerNetwork]: {
        subtitle: 'Trouvez un expert-comptable spécialisé dans votre domaine.',
        title: 'Réseau de comptables partenaires',
      },
      [RowKey.ToolBox]: {
        subtitle:
          'Accédez à des offres partenaires exclusives : assurances, logiciels comptables, placements...',
        title: 'Partenaires',
      },
    },
    sections: {
      [SectionKey.BankAndPayment]: 'Banque et paiements',
      [SectionKey.SpendManagement]: 'Gestion des dépenses',
      [SectionKey.SimplifiedComptability]: 'Comptabilité simplifiée',
      [SectionKey.Billing]: 'Facturation',
      [SectionKey.SupportAndInsurance]: 'Assistance et assurances',
    },
    standardSupport: 'Standard',
    virtualCardsIncluded: 'cartes virtuelles incluses',
  },
});

export default locales;
